import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Form, Image, Button, Checkbox } from 'semantic-ui-react';
import './CreateAd.scss';
import PropTypes from 'prop-types';
import addAdvertisement from 'redux/actions/communication/advertisement/addAdvertisement';
import getSingleAdvertisement from 'redux/actions/communication/advertisement/getSingleAdvertisement';
import updateAdvertisement from 'redux/actions/communication/advertisement/updateAdvertisement';
import notify from 'helpers/notificationHelper';
import { WARN } from 'constants/notificationStatus';
import MyDropzone from 'components/common/DragAndDrop';
import PageLoader from 'components/common/PageLoader';
import isURL from 'helpers/isURL';
import { compactObject } from 'helpers/removeEmptyProperties';

const options = [
  { key: 'tap_and_win', text: 'Tap & Win', value: 'tap_and_win' },
  {
    key: 'external_link',
    text: 'External link',
    value: 'external_link',
  },
];

const CreateAd = ({ editingAd, setEditingAd }) => {
  const [files, setFiles] = useState({
    lightSmall: null,
    lightLarge: null,
    darkSmall: null,
    darkLarge: null,
  });
  const [fileUrls, setFileUrls] = useState({
    lightSmall: null,
    lightLarge: null,
    darkSmall: null,
    darkLarge: null,
  });

  const [form, setForm] = useState({});

  const fileInputRefs = {
    light: {
      small: useRef(null),
      large: useRef(null),
    },
    dark: {
      small: useRef(null),
      large: useRef(null),
    },
  };

  const dispatch = useDispatch();
  const [header, setHeader] = useState('New Advertisement');

  const { loading } = useSelector(
    ({ advertisement: { addAdvertisement } }) => addAdvertisement,
  );
  const { loading: updateLoading } = useSelector(
    ({ advertisement: { updateAdvertisement } }) =>
      updateAdvertisement,
  );
  const { data: singleAd, loading: singleAdLoading } = useSelector(
    ({ advertisement: { singleAdvertisement } }) =>
      singleAdvertisement,
  );

  const handleChange = ({ target: { name, value } }) => {
    setForm({ ...form, [name]: value });
  };

  const handleClear = () => {
    setForm({ title: '', has_action: false });

    setFiles(prevFiles => ({
      ...prevFiles,
      lightSmall: null,
      lightLarge: null,
      darkSmall: null,
      darkLarge: null,
    }));
    setFileUrls(prevFiles => ({
      ...prevFiles,
      lightSmall: null,
      lightLarge: null,
      darkSmall: null,
      darkLarge: null,
    }));
    setEditingAd(null);
  };

  /**
   * @param {light|dark} mode
   * @param {small|large} size
   */
  const clickFileAttchement = (mode, size) => {
    fileInputRefs[mode][size].current.click();
  };

  const validateImageType = file => {
    if (!file) return false;

    const validTypes = [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif',
    ];
    const validExtensions = ['.png', '.jpg', '.jpeg', '.gif'];

    const fileName = file.name.toLowerCase();
    const hasValidType = validTypes.includes(file.type);
    const hasValidExtension = validExtensions.some(ext =>
      fileName.endsWith(ext),
    );

    return hasValidType || hasValidExtension;
  };

  const handleChangeAttach = e => {
    e.preventDefault();

    const name = e.target?.name;
    const file = e.target?.files[0];

    if (!validateImageType(file)) {
      notify({
        message:
          'Please upload a valid image file (PNG, JPG, JPEG, or GIF)',
        status: WARN,
      });
      e.target.value = null;
      return;
    }

    const objectUrl = URL.createObjectURL(e.target?.files[0]);

    setFiles(prevFiles => {
      return {
        ...prevFiles,
        [name]: file,
      };
    });

    setFileUrls(prevUrls => {
      return {
        ...prevUrls,
        [name]: objectUrl,
      };
    });

    return () => URL.revokeObjectURL(objectUrl);
  };

  const handleUpdate = () => {
    const formData = new FormData();

    if (files.lightSmall) {
      if (!validateImageType(files.lightSmall)) {
        return notify({
          message:
            'Light small file must be a valid image (PNG, JPG, JPEG, or GIF)',
          status: WARN,
        });
      }
      const isLessThan3MBs = files.lightSmall.size / 1024 / 1024 > 3;

      if (isLessThan3MBs) {
        return notify({
          message: 'Light file should not exceed 3 MBs',
          status: WARN,
        });
      }
      formData.append('light_small_image', files.lightSmall);
    }
    if (files.darkSmall) {
      if (!validateImageType(files.darkSmall)) {
        return notify({
          message:
            'Dark small file must be a valid image (PNG, JPG, JPEG, or GIF)',
          status: WARN,
        });
      }
      const isLessThan3MBs = files.darkSmall.size / 1024 / 1024 > 3;

      if (isLessThan3MBs) {
        return notify({
          message: 'Light file should not exceed 3 MBs',
          status: WARN,
        });
      }
      formData.append('dark_small_image', files.darkSmall);
    }
    if (files.lightLarge) {
      if (!validateImageType(files.lightLarge)) {
        return notify({
          message:
            'Light large file must be a valid image (PNG, JPG, JPEG, or GIF)',
          status: WARN,
        });
      }
      const isLessThan3MBs = files.lightLarge.size / 1024 / 1024 > 3;

      if (isLessThan3MBs) {
        return notify({
          message: 'Light file should not exceed 3 MBs',
          status: WARN,
        });
      }
      formData.append('light_large_image', files.lightLarge);
    }
    if (files.darkLarge) {
      if (!validateImageType(files.darkLarge)) {
        return notify({
          message:
            'Dark large file must be a valid image (PNG, JPG, JPEG, or GIF)',
          status: WARN,
        });
      }
      const isLessThan3MBs = files.darkLarge.size / 1024 / 1024 > 3;

      if (isLessThan3MBs) {
        return notify({
          message: 'Light file should not exceed 3 MBs',
          status: WARN,
        });
      }
      formData.append('dark_large_image', files.darkLarge);
    }

    if (!form.has_action) {
      form.action_content = undefined;
      form.action_label = undefined;
      form.action_type = undefined;
    }

    if (form.action_type === 'tap_and_win') {
      form.action_content = undefined;
    }

    const compactForm = compactObject(form);

    Object.keys(compactForm).forEach(key => {
      formData.append(key, compactForm[key]);
    });

    updateAdvertisement(editingAd, formData)(dispatch, () => {
      handleClear();
    });
  };

  const handleSubmit = () => {
    const formData = new FormData();

    // Validate image types
    if (!validateImageType(files.lightSmall)) {
      return notify({
        message:
          'Light small file must be a valid image (PNG, JPG, JPEG, or GIF)',
        status: WARN,
      });
    }
    if (!validateImageType(files.lightLarge)) {
      return notify({
        message:
          'Light large file must be a valid image (PNG, JPG, JPEG, or GIF)',
        status: WARN,
      });
    }
    if (!validateImageType(files.darkSmall)) {
      return notify({
        message:
          'Dark small file must be a valid image (PNG, JPG, JPEG, or GIF)',
        status: WARN,
      });
    }
    if (!validateImageType(files.darkLarge)) {
      return notify({
        message:
          'Dark large file must be a valid image (PNG, JPG, JPEG, or GIF)',
        status: WARN,
      });
    }

    const isBiggerThan3MBs = {
      darkLarge: files.darkLarge.size / 1024 / 1024 > 3,
      lightLarge: files.lightLarge.size / 1024 / 1024 > 3,
      darkSmall: files.darkSmall.size / 1024 / 1024 > 3,
      lightSmall: files.lightSmall.size / 1024 / 1024 > 3,
    };

    if (isBiggerThan3MBs.lightSmall) {
      return notify({
        message: 'Light small file should not exceed 3 MBs',
        status: WARN,
      });
    }
    if (isBiggerThan3MBs.lightLarge) {
      return notify({
        message: 'Light large file should not exceed 3 MBs',
        status: WARN,
      });
    }
    if (isBiggerThan3MBs.darkSmall) {
      return notify({
        message: 'Dark small file should not exceed 3 MBs',
        status: WARN,
      });
    }
    if (isBiggerThan3MBs.darkLarge) {
      return notify({
        message: 'Dark large file should not exceed 3 MBs',
        status: WARN,
      });
    }

    if (form.status === 'active') {
      formData.append('status', form.status);
    }

    if (!form.has_action) {
      form.action_content = undefined;
      form.action_label = undefined;
      form.action_type = undefined;
    }

    if (form.action_type === 'tap_and_win') {
      form.action_content = undefined;
    }

    const compactForm = compactObject(form);

    Object.keys(compactForm).forEach(key => {
      if (key !== 'status') {
        formData.append(key, compactForm[key]);
      }
    });

    formData.append('light_small_image', files.lightSmall);
    formData.append('dark_small_image', files.darkSmall);
    formData.append('light_large_image', files.lightLarge);
    formData.append('dark_large_image', files.darkLarge);

    addAdvertisement(formData)(dispatch, () => {
      handleClear();
    });
  };

  useEffect(() => {
    if (editingAd) {
      getSingleAdvertisement(editingAd)(dispatch);

      setHeader('Editing Advertisement');
    }
    setForm({});
  }, [editingAd]);

  const isButtonDisabled = () => {
    if (editingAd) {
      return (
        !form?.title ||
        form?.title === '' ||
        updateLoading ||
        (form.has_action === true &&
          (!form.action_type ||
            form.action_type === '' ||
            !form.action_label ||
            (form.action_type === 'external_link' &&
              (!form.action_content || !isURL(form.action_content)))))
      );
    }
    return (
      !files.darkLarge ||
      !files.lightLarge ||
      !files.darkSmall ||
      !files.lightSmall ||
      !form?.title ||
      form?.title === '' ||
      loading ||
      (form.has_action === true &&
        (!form.action_type ||
          form.action_type === '' ||
          !form.action_label ||
          (form.action_type === 'external_link' &&
            (!form.action_content || !isURL(form.action_content)))))
    );
  };

  useEffect(() => {
    if (singleAd) {
      setForm({
        ...form,
        action_content: singleAd.action_content,
        action_label: singleAd.action_label,
        action_type: singleAd.action_type,
        has_action: singleAd.has_action,
        title: singleAd.title,
        status: singleAd.status,
      });
    }
  }, [singleAd]);

  return (
    <div className="create-new-ad">
      <>
        {!!form?.title || files.lightSmall ? (
          <div className="header">
            <h3>{header}</h3>
            <Button
              content="Clear"
              className="header__clear"
              onClick={() => {
                handleClear();
                setHeader('New Advertisement');
              }}
            />
          </div>
        ) : (
          <h3>{header}</h3>
        )}
        {editingAd ? (
          singleAdLoading ? (
            <PageLoader />
          ) : (
            <Form className="create-ad-form">
              <div className="form-dropzones">
                <div className="form-dropzones__title">
                  Light mode
                </div>
                <div className="form-dropzones__inner">
                  <div>
                    <span>Small image (Light mode)</span>
                    <div className="editing_image">
                      <Image
                        file
                        name="lightSmall"
                        src={
                          files.lightSmall
                            ? fileUrls.lightSmall
                            : singleAd?.light_small_image
                        }
                        alt=""
                        className="editing_imaged__file"
                      />
                      <input
                        className="attachment"
                        ref={fileInputRefs.light.small}
                        type="file"
                        name="lightSmall"
                        required
                        accept="image/*"
                        onChange={handleChangeAttach}
                      />

                      <button
                        className="editing_image__button"
                        type="button"
                        onClick={() =>
                          clickFileAttchement('light', 'small')
                        }
                      >
                        Change Image
                      </button>
                    </div>
                  </div>

                  <div>
                    <span>Small image (Dark mode)</span>
                    <div className="editing_image">
                      <Image
                        file
                        name="darkSmall"
                        src={
                          files.darkSmall
                            ? fileUrls.darkSmall
                            : singleAd?.dark_small_image
                        }
                        alt=""
                        className="editing_imaged__file"
                      />
                      <input
                        className="attachment"
                        name="darkSmall"
                        ref={fileInputRefs.dark.small}
                        type="file"
                        required
                        accept="image/*"
                        onChange={handleChangeAttach}
                      />

                      <button
                        className="editing_image__button"
                        type="button"
                        onClick={() =>
                          clickFileAttchement('dark', 'small')
                        }
                      >
                        Change Image
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-dropzones">
                <div className="form-dropzones__title">Dark mode</div>
                <div className="form-dropzones__inner">
                  <div>
                    <span>Large image (Light mode)</span>
                    <div className="editing_image">
                      <Image
                        file
                        name="lightLarge"
                        src={
                          files.lightLarge
                            ? fileUrls.lightLarge
                            : singleAd?.light_large_image
                        }
                        alt=""
                        className="editing_imaged__file"
                      />
                      <input
                        className="attachment"
                        name="lightLarge"
                        ref={fileInputRefs.light.large}
                        type="file"
                        required
                        accept="image/*"
                        onChange={handleChangeAttach}
                      />

                      <button
                        className="editing_image__button"
                        type="button"
                        onClick={() =>
                          clickFileAttchement('light', 'large')
                        }
                      >
                        Change Image
                      </button>
                    </div>
                  </div>

                  <div>
                    <span>Large image (Dark mode)</span>
                    <div className="editing_image">
                      <Image
                        file
                        name="darkLarge"
                        src={
                          files.darkLarge
                            ? fileUrls.darkLarge
                            : singleAd?.dark_large_image
                        }
                        alt=""
                        className="editing_imaged__file"
                      />
                      <input
                        className="attachment"
                        name="darkLarge"
                        ref={fileInputRefs.dark.large}
                        type="file"
                        required
                        accept="image/*"
                        onChange={handleChangeAttach}
                      />

                      <button
                        className="editing_image__button"
                        type="button"
                        onClick={() =>
                          clickFileAttchement('dark', 'large')
                        }
                      >
                        Change Image
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-inputs">
                <Form.Input
                  onChange={handleChange}
                  required
                  className="input_title"
                  defaultValue={singleAd?.title}
                  label="Subject"
                  name="title"
                  placeholder="Subject"
                />

                <div className="action-group">
                  <span>Does this add has action ?</span>
                  <div className="action-group__checkbox">
                    <Checkbox
                      toggle
                      className="publish-checkbox"
                      checked={form.has_action}
                      onChange={(_, { checked }) =>
                        setForm({
                          ...form,
                          has_action: checked,
                        })
                      }
                    />
                  </div>
                </div>

                {form.has_action && (
                  <>
                    <Form.Select
                      fluid
                      required
                      onChange={(_, { value }) => {
                        setForm({
                          ...form,
                          action_type: value,
                        });
                      }}
                      defaultValue={singleAd?.action_type}
                      options={options}
                      label="Action type"
                      name="action_type"
                      placeholder="Action type"
                    />
                    {form.action_type === 'external_link' && (
                      <Form.Input
                        fluid
                        required
                        onChange={handleChange}
                        defaultValue={singleAd?.action_content}
                        label="Link"
                        name="action_content"
                        placeholder="External link"
                      />
                    )}
                    <Form.Input
                      fluid
                      required
                      onChange={handleChange}
                      defaultValue={singleAd?.action_label}
                      label="Button label"
                      name="action_label"
                      placeholder="Button label"
                    />
                  </>
                )}

                <div className="send-btn-wrapper">
                  <Form.Button
                    primary
                    className="create"
                    disabled={isButtonDisabled()}
                    loading={updateLoading}
                    onClick={handleUpdate}
                  >
                    Save
                  </Form.Button>
                </div>
              </div>
            </Form>
          )
        ) : (
          <Form onSubmit={handleSubmit} className="create-ad-form">
            <div className="form-dropzones">
              <div className="form-dropzones__title">Light mode</div>
              <div className="form-dropzones__inner">
                <MyDropzone
                  label="Large image"
                  setFile={file =>
                    setFiles(prev => ({
                      ...prev,
                      lightLarge: file,
                    }))
                  }
                  file={files.lightLarge}
                  name="lightLarge"
                  handleChangeAttach={handleChangeAttach}
                  clickFileAttchement={() =>
                    clickFileAttchement('light', 'large')
                  }
                  fileInputRef={fileInputRefs.light.large}
                  accept="image/png,image/jpeg,image/gif"
                />
                <MyDropzone
                  label="Small image"
                  setFile={file =>
                    setFiles(prev => ({
                      ...prev,
                      lightSmall: file,
                    }))
                  }
                  file={files.lightSmall}
                  name="lightSmall"
                  handleChangeAttach={handleChangeAttach}
                  clickFileAttchement={() =>
                    clickFileAttchement('light', 'small')
                  }
                  fileInputRef={fileInputRefs.light.small}
                  accept="image/png,image/jpeg,image/gif"
                />
              </div>
            </div>
            <div className="form-dropzones">
              <div className="form-dropzones__title">Dark mode</div>
              <div className="form-dropzones__inner">
                <MyDropzone
                  label="Large image"
                  setFile={file =>
                    setFiles(prev => ({
                      ...prev,
                      darkLarge: file,
                    }))
                  }
                  file={files.darkLarge}
                  name="darkLarge"
                  handleChangeAttach={handleChangeAttach}
                  clickFileAttchement={() =>
                    clickFileAttchement('dark', 'large')
                  }
                  fileInputRef={fileInputRefs.dark.large}
                  accept="image/png,image/jpeg,image/gif"
                />
                <MyDropzone
                  label="Small image"
                  setFile={file =>
                    setFiles(prev => ({
                      ...prev,
                      darkSmall: file,
                    }))
                  }
                  file={files.darkSmall}
                  name="darkSmall"
                  handleChangeAttach={handleChangeAttach}
                  clickFileAttchement={() =>
                    clickFileAttchement('dark', 'small')
                  }
                  fileInputRef={fileInputRefs.dark.small}
                  accept="image/png,image/jpeg,image/gif"
                />
              </div>
            </div>

            <div className="form-inputs">
              <Form.Input
                onChange={handleChange}
                required
                value={form.title}
                label="Subject"
                name="title"
                placeholder="Subject"
              />
              <div className="action-group">
                <span>Does this add has action ?</span>
                <div className="action-group__checkbox">
                  <Checkbox
                    toggle
                    className="publish-checkbox"
                    checked={form.has_action}
                    onChange={(_, { checked }) =>
                      setForm({
                        ...form,
                        has_action: checked,
                      })
                    }
                  />
                </div>
              </div>

              {form.has_action && (
                <>
                  <Form.Select
                    fluid
                    required
                    onChange={(_, { value }) => {
                      setForm({
                        ...form,
                        action_type: value,
                      });
                    }}
                    value={form.action_type}
                    options={options}
                    label="Action type"
                    name="action_type"
                    placeholder="Action type"
                  />
                  {form.action_type === 'external_link' && (
                    <Form.Input
                      fluid
                      required
                      onChange={handleChange}
                      value={form.link}
                      label="Link"
                      name="action_content"
                      placeholder="External link"
                    />
                  )}
                  <Form.Input
                    fluid
                    required
                    onChange={handleChange}
                    value={form.action_label}
                    label="Button label"
                    name="action_label"
                    placeholder="Button label"
                  />
                </>
              )}

              <div className="send-btn-wrapper">
                <Checkbox
                  toggle
                  label="publish"
                  className="publish-checkbox"
                  checked={form.status === 'active'}
                  onChange={(_, { checked }) =>
                    setForm({
                      ...form,
                      status: checked ? 'active' : 'inactive',
                    })
                  }
                />
                <Form.Button
                  primary
                  loading={loading}
                  type="submit"
                  className="create"
                  disabled={isButtonDisabled()}
                >
                  Create Ad
                </Form.Button>
              </div>
            </div>
          </Form>
        )}
      </>
    </div>
  );
};

CreateAd.defaultProps = {};

CreateAd.propTypes = {
  editingAd: PropTypes.number.isRequired,
  setEditingAd: PropTypes.func.isRequired,
};
export default CreateAd;
